import React, {useRef, useState} from "react";
import {Col, Form, Input, Modal, Radio, Row} from "antd";
import {BellOutlined} from "@ant-design/icons";
import moment from "moment";
import {useFetch} from "hooks/useFetch";
import {useStore} from "hooks/useStore";
import {normalizeCityName} from "utils/normalizeCityName";
import {AnnouncementPreview} from "./announcementPreview/AnnouncementPreview";
import {FormatHint} from "./announcementPreview/AnnouncementPreview.styles";
import {sanitizeWhatsappText} from "./announcementPreview/sanitizeWhatsappText";
import {sanitizeWhatsappPaste} from "./announcementPreview/sanitizeWhatsappPaste";

const buildQuery = filterParams => ({
    cityId: filterParams.cityId ? normalizeCityName(filterParams.cityId) : undefined,
    continent: filterParams.continent,
    preferredCityId: filterParams.preferredCity
        ? normalizeCityName(filterParams.preferredCity)
        : undefined,
    preferredContinent: filterParams.preferredContinent || undefined,
    types: filterParams.types || [],
    searchText: filterParams.searchText || "",
    vehicleInfo: filterParams.vehicleInfo || "",
    tckn: filterParams.tckn || "",
    activity: filterParams.activity,
    sort: filterParams.sort || [],
    activationStep: filterParams.activationStep,
    activationSubStep: filterParams.activationSubStep,
    courierCounties: filterParams.districts || [],
    preferredDistrictIds: filterParams.preferredDistricts || [],
    startDate:
        Array.isArray(filterParams.selectedDateRange) &&
        filterParams.selectedDateRange.length === 2 &&
        filterParams.selectedDateRange[0] &&
        filterParams.selectedDateRange[1]
            ? moment(filterParams.selectedDateRange[0]).format("YYYY-MM-DD")
            : undefined,
    endDate:
        Array.isArray(filterParams.selectedDateRange) &&
        filterParams.selectedDateRange.length === 2 &&
        filterParams.selectedDateRange[0] &&
        filterParams.selectedDateRange[1]
            ? moment(filterParams.selectedDateRange[1]).format("YYYY-MM-DD")
            : undefined,
    walletOwnership: filterParams.walletOwnershipFilter || "all",
    callStatus: filterParams.callStatus,
    contractSigningStatus: filterParams.contractSigningStatus,
    motorTypeIds: filterParams.motorTypeIds || [],
    drivingLicenseIds: filterParams.drivingLicenseIds || [],
    workingModelId: filterParams.workingModelId || [],
    hasCriminalRecord: filterParams.hasCriminalRecord,
    is20Old: filterParams.is20Old,
    personalCompanySetupStatus:
        filterParams.personalCompanySetupStatus === "all"
            ? undefined
            : filterParams.personalCompanySetupStatus,
    autoValidationStatus:
        filterParams.autoValidationStatus === "all" ? undefined : filterParams.autoValidationStatus,
    zoneManagementId: filterParams.zoneManagementId,
    employmentType: filterParams.employmentType
});

const TITLE_MAX_LENGTH = 100;
const MESSAGE_MAX_LENGTH = 1500;

export const SendAnnouncementModal = ({visible, onClose, filterParams, allowedChannels}) => {
    const stores = useStore();
    const [form] = Form.useForm();
    const announcementRef = useRef(null);
    // Kullanıcı sadece bir kanala yetkiliyse (ör. yalnızca whatsapp), o kanal varsayılan seçilir
    const defaultChannel = allowedChannels?.app ? "app" : "whatsapp";
    const initialPreviewState = {channel: defaultChannel, title: "", message: ""};
    const [preview, setPreview] = useState(initialPreviewState);

    const [loading, , , , refetch] = useFetch({
        fetchFn: () =>
            announcementRef.current.channel === "whatsapp"
                ? stores.courierStore.sendWhatsappToFilteredCouriers(
                      announcementRef.current.payload
                  )
                : stores.courierStore.sendAnnouncementToFilteredCouriers(
                      announcementRef.current.payload
                  ),
        initialCall: false,
        callbackFunc: result => {
            if (result?.success) {
                form.resetFields();
                setPreview(initialPreviewState);
                onClose();
            }
        }
    });

    const handleValuesChange = (_, allValues) => {
        const isWhatsapp = allValues.channel === "whatsapp";
        setPreview({
            channel: allValues.channel,
            title: isWhatsapp ? sanitizeWhatsappText(allValues.title) : allValues.title,
            message: isWhatsapp ? sanitizeWhatsappText(allValues.message) : allValues.message
        });
    };

    // WhatsApp şablonu satır sonu kabul etmediği için Enter ile yeni satıra geçilemiyor
    const handleMessageKeyDown = event => {
        if (event.key === "Enter" && preview.channel === "whatsapp") {
            event.preventDefault();
        }
    };

    const handlePaste = (fieldName, maxLength) => event => {
        if (preview.channel !== "whatsapp") return;
        const nextValue = sanitizeWhatsappPaste(event, maxLength);
        if (nextValue === null) return;
        form.setFieldsValue({[fieldName]: nextValue});
        handleValuesChange(null, form.getFieldsValue());
    };

    const handleConfirm = async () => {
        try {
            const values = await form.validateFields();
            const isWhatsapp = values.channel === "whatsapp";
            // BE her iki kanal için de aynı gövde şeklini bekliyor
            const payload = {
                query: buildQuery(filterParams),
                title: isWhatsapp
                    ? sanitizeWhatsappText(values.title).trim()
                    : values.title,
                body: isWhatsapp
                    ? sanitizeWhatsappText(values.message).trim()
                    : values.message
            };
            announcementRef.current = {channel: values.channel, payload};
            refetch();
        } catch (error) {
            // form validation hatası, refetch tetiklenmez
        }
    };

    const handleCancel = () => {
        form.resetFields();
        setPreview(initialPreviewState);
        onClose();
    };

    return (
        <Modal
            visible={visible}
            title={
                <span>
                    <BellOutlined style={{marginRight: 8}} />
                    Filtrelenen Kuryelere Duyuru Gönder
                </span>
            }
            okText="Filtreli Kuryelere Gönder"
            cancelText="İptal"
            centered
            destroyOnClose
            width={900}
            confirmLoading={loading}
            onOk={handleConfirm}
            onCancel={handleCancel}
        >
            <Row gutter={24}>
                <Col xs={24} md={12}>
                    <Form
                        form={form}
                        layout="vertical"
                        initialValues={initialPreviewState}
                        onValuesChange={handleValuesChange}
                    >
                        <Form.Item
                            name="channel"
                            label="Gönderim Kanalı"
                            rules={[{required: true, message: "Gönderim kanalı zorunludur"}]}
                        >
                            <Radio.Group>
                                {allowedChannels?.app && (
                                    <Radio value="app">Vigo Kurye uygulamasına bildirim</Radio>
                                )}
                                {allowedChannels?.whatsapp && (
                                    <Radio value="whatsapp">WhatsApp mesajı</Radio>
                                )}
                            </Radio.Group>
                        </Form.Item>
                        <Form.Item
                            name="title"
                            label="Başlık"
                            rules={[{required: true, message: "Başlık zorunludur"}]}
                        >
                            <Input
                                placeholder="Duyuru başlığını giriniz"
                                maxLength={TITLE_MAX_LENGTH}
                                showCount
                                onPaste={handlePaste("title", TITLE_MAX_LENGTH)}
                            />
                        </Form.Item>
                        <Form.Item
                            name="message"
                            label="Mesaj"
                            rules={[{required: true, message: "Mesaj zorunludur"}]}
                        >
                            <Input.TextArea
                                rows={4}
                                placeholder="Duyuru mesajını giriniz"
                                maxLength={MESSAGE_MAX_LENGTH}
                                showCount
                                onKeyDown={handleMessageKeyDown}
                                onPaste={handlePaste("message", MESSAGE_MAX_LENGTH)}
                            />
                        </Form.Item>
                        {preview.channel === "whatsapp" ? (
                            <FormatHint>
                                Kalın için <strong>*Kalın Yazı*</strong>, italik için{" "}
                                <em>_İtalik Yazı_</em> kullanabilirsiniz.
                            </FormatHint>
                        ) : (
                            <FormatHint>
                                Kalın için <strong>&lt;b&gt;Kalın Yazı&lt;/b&gt;</strong>, kurye
                                adı için <strong>[Vigolu]</strong> kullanabilirsiniz.
                            </FormatHint>
                        )}
                    </Form>
                </Col>
                <Col xs={24} md={12}>
                    <AnnouncementPreview
                        channel={preview.channel}
                        title={preview.title}
                        message={preview.message}
                    />
                </Col>
            </Row>
        </Modal>
    );
};
